import type { DirectoryUser } from "@/lib/types";
import {
  type ReportsMatterSearchTableRow,
  type ReportsOpenMattersReport,
} from "@/lib/types";
import { resolveReportPrimaryStatute } from "@/lib/utils/matters/report-statute-utils";

const normalizeValues = (values?: string[] | null) => {
  if (!values) return [];
  return Array.from(
    new Set(
      values
        .map((value) => value.trim())
        .filter((value): value is string => Boolean(value)),
    ),
  );
};

const normalizeUnknownValues = (value?: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return normalizeValues(
      value
        .filter(
          (item): item is string | number =>
            typeof item === "string" || typeof item === "number",
        )
        .map((item) => String(item)),
    );
  }
  if (typeof value === "string" || typeof value === "number") {
    return normalizeValues([String(value)]);
  }
  return [];
};

export const mapOpenMattersReportToTableRow = (
  report: ReportsOpenMattersReport,
  users: DirectoryUser[] = [],
): ReportsMatterSearchTableRow | null => {
  const reportValues = report as Record<string, unknown>;
  const matterId = report.openMattersReportMatterId;
  if (!matterId) {
    return null;
  }

  const assigneeIds = normalizeValues(report.openMattersReportMatterAssignee);
  const assignees = assigneeIds.flatMap((id) => {
    const user = users.find((candidate) => candidate.id === id);
    if (!user) {
      return [];
    }
    const firstName = user.firstName.trim();
    const lastName = user.lastName.trim();
    if (!firstName && !lastName) {
      return [];
    }
    return [{ firstName: firstName, lastName: lastName }];
  });

  const teamLabels = normalizeValues(report.openMattersReportMatterTeam);
  const tagLabels = normalizeValues(report.openMattersReportMatterTag);
  const defendantNames = normalizeUnknownValues(
    reportValues.openMattersReportMatterDefendantName ??
      reportValues.openMattersReportMatterDefendantNames ??
      reportValues.matterDefendantName ??
      reportValues.matterDefendantNames,
  );
  const insuranceClaimNumbers = normalizeUnknownValues(
    reportValues.openMattersReportclaimNumber ??
      reportValues.openMattersReportclaimNumbers ??
      reportValues.claimNumber,
  );
  const insurancePolicyNumbers = normalizeUnknownValues(
    reportValues.openMattersReportpolicyNumber ??
      reportValues.openMattersReportpolicyNumbers ??
      reportValues.policyNumber,
  );
  const primaryStatute = resolveReportPrimaryStatute(
    report.openMattersReportMatterStatuteOfLimitations,
    report.openMattersReportMatterStatuteOfLimitationsConfirmed ?? null,
  );

  return {
    claimTypes: report.claimTypes ?? [],
    matterId,
    dateCreated: report.dateCreated ?? "",
    dateModified: report.dateModified ?? "",
    matterTitle: report.openMattersReportMatterTitle ?? null,
    matterStatusLabel: report.openMattersReportMatterStatusLabel ?? null,
    matterStatusCategory: report.openMattersReportMatterStatusCategory ?? null,
    matterDefendantName: defendantNames,
    claimNumber: insuranceClaimNumbers,
    policyNumber: insurancePolicyNumbers,
    medicalTreatmentBillTotal:
      report.openMattersReportMedicalTreatmentBillTotal ?? null,
    matterEmailAddress: null,
    matterDateOfLoss: report.openMattersReportMatterDateOfLoss ?? null,
    matterStatuteOfLimitations: primaryStatute.matterStatuteOfLimitations,
    matterStatuteOfLimitationsConfirmed:
      primaryStatute.matterStatuteOfLimitationsConfirmed,
    matterIncidentAddressCity: "",
    matterIncidentAddressState:
      report.openMattersReportMatterIncidentState ?? "",
    matterIncidentAddressStreet1: "",
    matterIncidentAddressStreet2: "",
    matterIncidentAddressZip: "",
    matterIncidentAddressCountry: "",
    matterContactConnection: [],
    matterAssignee: assignees,
    matterTeamConnection: teamLabels.map((label) => ({
      label: label,
      matterTeamConnectionDateTimeEnd: undefined,
    })),
    matterTagConnection: tagLabels.map((label) => ({
      label: label,
    })),
    incidentTypeLabel: report.openMattersReportIncidentTypeLabel ?? "",
  };
};
